/**
 * Domain 7: Practical & Social Reasoning (الاستدلال العملي والاجتماعي)
 * 8 Modular Template Families with rich multi-scenario banks testing fluid situational judgment,
 * perspective taking, decision theory, cognitive biases, and pragmatic logic.
 * Balanced option lengths (words & characters) eliminate length-heuristic bias.
 */

export const SOCIAL_TEMPLATES = [
  // 1. Conflict De-escalation Under Deadline Pressure
  {
    id: 'social_conflict_resolution',
    domain: 'practical_social',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'قبل موعد تسليم مشروع جماعي بيومين، اختلف عضوان في الفريق بشدة حول كيفية عرض النتائج النهائية، وبدأ النقاش يعطل تقدم العمل.',
          scenario_en: 'Two days before a critical group project deadline, two members engage in a heated dispute regarding how final results should be presented, halting work progress.',
          best_ar: 'تحديد النقاط المتفق عليها أولاً، ثم تقسيم الأجزاء المتبقية لاختبار الخيارين بسرعة بناءً على معايير تسليم المشروع.',
          best_en: 'Identify points of agreement first, then split remaining tasks to test both options against the submission rubric.',
          distractors_ar: [
            'الاعتماد على تصويت الأغلبية بين باقي أعضاء الفريق دون مراجعة الجوانب الفنية للخيارين لضمان التسليم في الموعد.',
            'إلزام الطرفين بدمج كل المقترحات معًا في عرض تقديمي واحد مطول رغم تضارب التنسيق تجنبًا لخلافات إضافية.',
            'تصعيد الخلاف مباشرة إلى المشرف العام ليتخذ القرار بدلاً من الفريق وتجميد العمل حتى صدور التوجيه النهائي.'
          ],
          distractors_en: [
            'Call a quick majority vote among other teammates without evaluating technical merits to guarantee meeting the deadline.',
            'Compel both sides to combine all proposals into a single bloated presentation despite formatting clashes to keep peace.',
            'Escalate the dispute directly to the lead supervisor for a ruling and freeze further progress until directed.'
          ],
          explanation_ar: 'النهج الأكثر فاعلية يجمع بين احتواء المشاعر بالتركيز على المشتركات، والتحكيم الموضوعي وفق معايير المشروع المحددة.',
          explanation_en: 'The most effective strategy de-escalates emotional friction by establishing common ground and anchoring choices to objective criteria.'
        },
        {
          scenario_ar: 'تصاعد الخلاف بين فريق تطوير البرمجيات الساعي لإطلاق ميزات سريعة، وفريق ضمان الجودة المتمسك بفترة فحص طويلة وصارمة.',
          scenario_en: 'Tension rises between a development team pushing for rapid feature release and a quality assurance team demanding prolonged, stringent testing cycles.',
          best_ar: 'تصنيف الميزات حسب درجة خطورتها: إطلاق الميزات الآمنة فورًا مع إخضاع الأجزاء الحساسة لاختبارات جودة مكثفة.',
          best_en: 'Triage features by risk severity: release low-risk modules immediately while subjecting critical components to rigorous testing.',
          distractors_ar: [
            'تمديد مهلة التسليم بالكامل وتجميد كل الإطلاقات حتى ينتهي فريق الجودة من فحص كل تفصيلة بدقة متناهية.',
            'المضي في إطلاق النسخة كاملة دون انتظار الجودة مع تخصيص فريق طوارئ لمعالجة المشكلات التقنية اللاحقة في الإنتاج.',
            'تقليص عدد حالات الاختبار بنسبة النصف كحل وسط يرضي الطرفين دون تحليل منهجي لدرجة الخطورة الفنية للميزات.'
          ],
          distractors_en: [
            'Extend the entire release deadline and freeze all deployments until every low-priority test script completes.',
            'Push the complete release immediately and task an emergency on-call team to patch failures live in production.',
            'Cut the test suite in half as an arbitrary compromise without analyzing the technical risk profile of each feature.'
          ],
          explanation_ar: 'التوفيق بين السرعة والجودة يعتمد على تقييم المخاطر وتفكيك المشكلة إلى مستويات أمان متفاوتة بدلاً من الحلول الحدية المطلقة.',
          explanation_en: 'Balancing velocity and stability relies on risk stratification rather than all-or-nothing trade-offs.'
        },
        {
          scenario_ar: 'خلال اجتماع عام، وجه أحد الخبراء نقدًا حادًا وشخصيًا لمقترح زميل له أمام أعضاء الفريق المبتدئين، مما خلق توترًا صامتًا.',
          scenario_en: 'During an all-hands meeting, a senior expert delivers a sharp, personal critique of a colleague’s proposal in front of junior staff, creating awkward tension.',
          best_ar: 'إعادة توجيه النقاش فورًا إلى البيانات الموضوعية للمقترح، ثم معالجة الأسلوب الحاد في جلسة خاصة ثنائية بعد الاجتماع.',
          best_en: 'Refocus the discussion immediately on objective data, then address the interpersonal tone privately after the meeting.',
          distractors_ar: [
            'الاعتراض الحاد علنًا أثناء الاجتماع وإيقاف العرض فورًا لإلزام الخبير بتقديم اعتذار رسمي أمام كافة الحاضرين.',
            'تجاهل الملاحظات تمامًا ومتابعة الاجتماع بشكل روتيني دون اتخاذ أي إجراء لحماية سلامة بيئة النقاشات المستقبلية.',
            'إنهاء الاجتماع مبكرًا وتحويل مناقشة المقترح إلى مراسلات كتابية مغلقة لتفادي أي احتكاك إضافي بين الطرفين.'
          ],
          distractors_en: [
            'Interrupt the meeting aggressively to demand an immediate public apology from the senior expert on the spot.',
            'Ignore the outburst completely and proceed routinely without addressing the psychological safety of the environment.',
            'Adjourn the session prematurely and move the evaluation to private emails to circumvent further spoken friction.'
          ],
          explanation_ar: 'إدارة النزاع الاحترافية تفصل بين محتوى الفكرة وسلوك الأفراد، وتمنع تصعيد الخلافات الشخصية أمام الحضور مع حفظ كرامة الجميع.',
          explanation_en: 'Professional conflict resolution separates substance from interpersonal tone, avoiding public escalation while addressing conduct privately.'
        },
        {
          scenario_ar: 'اشتكى عميل غاضب من عدم مطابقة الخدمة لتوقعاته نتيجة وعود غير دقيقة قدمها فريق المبيعات ولم يستطع فريق التنفيذ الوفاء بها.',
          scenario_en: 'An irate client complains that delivered services fail to meet expectations due to inaccurate promises made by sales that operations cannot fulfill.',
          best_ar: 'الاستماع لاحتياجات العميل الحالية وتقديم خطة تعديل واقعية مع مواءمة المعايير بين المبيعات والتنفيذ لمنع تكرار الخطأ.',
          best_en: 'Listen to the client’s core needs, deliver a realistic adjustment plan, and realign sales with operations to prevent recurrence.',
          distractors_ar: [
            'التبرؤ التام أمام العميل من وعود المبيعات والتمسك بنصوص بنود العقد المكتوبة دون تقديم أي بدائل مرنة للحل.',
            'الموافقة الفورية على كافة مطالب العميل دون الرجوع لفريق التنفيذ وتحمل تكاليف تشغيلية باهظة وغير محسوبة.',
            'توجيه العميل لتقديم شكوى رسمية إلى الإدارة العليا لإعادة التفاوض بدلاً من معالجة المشكلة التشغيلية ميدانيًا.'
          ],
          distractors_en: [
            'Disavow the sales representations before the client and enforce the printed contract terms without exploring accommodations.',
            'Grant every client request immediately without consulting engineering, absorbing open-ended unbudgeted operational costs.',
            'Refer the client to executive management for contract renegotiation instead of resolving the operational gap directly.'
          ],
          explanation_ar: 'الحل الناضج يقدم واجهة مؤسسية موحدة أمام العميل، ويعالج الفجوة الحالية عمليًا مع إصلاح الخلل الداخلي بين الأقسام.',
          explanation_en: 'A mature response presents a unified organizational front, resolves the immediate discrepancy, and fixes internal misalignment.'
        },
        {
          scenario_ar: 'أدى استخدام الرسائل النصية المقتضبة في التواصل عن بُعد إلى شعور أحد الموظفين بأن مديره يستهدفه بسلبية ويتجاهل جهوده.',
          scenario_en: 'Brief text messages in remote communication led an employee to believe their manager is deliberately hostile and dismissive of their efforts.',
          best_ar: 'طلب مكالمة مرئية أو صوتية سريعة ومباشرة لإزالة سوء الفهم واستيضاح النبرة في بيئة حوار تفاعلية.',
          best_en: 'Request a brief video or voice check-in to clear ambiguous tone in an interactive conversation.',
          distractors_ar: [
            'إرسال بريد إلكتروني تفصيلي مطول يسرد كافة المساهمات السابقة للموظف لإثبات كفاءته وتفنيد النبرة المقتضبة.',
            'الرد بأسلوب مقتضب مماثل والاكتفاء بالحد الأدنى من التواصل المهني لتجنب أي احتكاك سلبي محتمل.',
            'التحدث مع زملاء الفريق الآخرين للتحقق مما إذا كان المدير يتعامل معهم بنفس الأسلوب المقتضب في العمل.'
          ],
          distractors_en: [
            'Draft a lengthy manifesto recounting all recent deliverables to formally counter the manager’s brevity.',
            'Mirror the curt communication style and reduce proactive updates to the absolute bare minimum required.',
            'Poll other remote teammates to investigate whether the manager displays similar terse patterns with everyone.'
          ],
          explanation_ar: 'التواصل الكتابي يفتقر إلى نبرة الصوت وتعبيرات الوجه؛ والانتقال لقناة تواصل أغنى (صوت أو فيديو) يبدد الشكوك وسوء التأويل سريعًا.',
          explanation_en: 'Text communication lacks nuance; switching to a richer synchronous medium quickly resolves misperceptions.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في الموقف التالي: "${item.scenario_ar}" — ما هو الإجراء الأكثر نضجًا وفعالية للتعامل مع هذا الموقف؟`,
        questionText_en: `In this situation: "${item.scenario_en}" — What is the most mature and effective approach to handle this situation?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  },

  // 2. Resource Triage Prioritization
  {
    id: 'social_resource_triage',
    domain: 'practical_social',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'لديك 4 ساعات فقط متبقية في يوم العمل، ووصلتك 4 مهام: (1) مشكلة فنية تعطل مستخدمين حاليين، (2) مراجعة تقرير لموعد بعد أسبوع، (3) الرد على استفسار عام روتيني، (4) حضور اجتماع غير طارئ يمكن قراءة ملخصه.',
          scenario_en: 'You have 4 hours left in your workday and receive 4 demands: (1) Technical bug affecting active users, (2) Reviewing a report due next week, (3) Answering a routine inquiry, (4) Attending a non-urgent meeting with notes available later.',
          best_ar: 'حل العطل الفني المؤثر على المستخدمين فوراً، وتفويض أو جدولة المهام الثلاث الأخرى وفق خطة منظمة.',
          best_en: 'Resolve the active user-impacting bug immediately, while scheduling or delegating the other three systematically.',
          distractors_ar: [
            'البدء بالاستفسار السريع والاجتماع أولاً لإنجاز أكبر عدد ممكن من البنود الورقية وإظهار سرعة الإنجاز الشكلي.',
            'توزيع الساعات الأربع المتبقية بالتساوي (ساعة واحدة لكل مهمة) بغض النظر عن مدى الأثر التشغيلي لكل منها.',
            'تأجيل معالجة العطل الفني إلى بداية يوم العمل التالي للتمكن من فحص أسبابه الجذرية بكامل التركيز والراحة.'
          ],
          distractors_en: [
            'Knock out the routine inquiry and meeting first to clear a higher numerical count of items off the queue.',
            'Divide the 4 hours uniformly (one hour per item) regardless of the differing operational severity involved.',
            'Postpone addressing the critical bug until tomorrow morning so it can be investigated with fresh morning focus.'
          ],
          explanation_ar: 'المهمة ذات الأثر المباشر والحرجة على المستخدمين تأتي دائمًا في المقام الأول في مصفوفة الأولويات (هام وعاجل).',
          explanation_en: 'Critical tasks with immediate disruption to users always take precedence in the Eisenhower priority matrix (urgent & important).'
        },
        {
          scenario_ar: 'انقطع التيار الكهربائي فجأة في مستشفى، وبدأ المولد الاحتياطي بالعمل بطاقة استيعابية محدودة تكفي لتشغيل 3 مرافق فقط من أصل 5.',
          scenario_en: 'A hospital suffers a sudden power outage. The backup generator can sustain only 3 out of 5 key facilities simultaneously.',
          best_ar: 'تشغيل غرف العناية المركزة، وغرف العمليات الجراحية، وثلاجات بنك الدم، وتأجيل المكاتب الإدارية وقاعات الانتظار.',
          best_en: 'Power the intensive care unit, surgical theaters, and blood bank refrigeration, while suspending administrative offices and waiting lounges.',
          distractors_ar: [
            'تشغيل قاعات الاستقبال وقسم تكنولوجيا المعلومات لضمان استمرار شبكة الاتصال وسجلات المرضى الرقمية أولاً.',
            'تدوير الطاقة الكهربائية دورياً بين المرافق الخمسة بالتساوي كل عشر دقائق لضمان عدم حرمان أي قسم من الإضاءة.',
            'إبقاء المولد في وضع الاستعداد دون تفعيل حتى يتم تقييم العطل الكهربائي الرئيسي بدقة لتجنب استهلاك الوقود الاحتياطي.'
          ],
          distractors_en: [
            'Power the reception halls and IT server room first to maintain digital records and communications for visitors.',
            'Rotate electricity cyclically across all 5 facilities every ten minutes to ensure fair distribution of light.',
            'Keep the backup generator on standby without load until the main fault is fully diagnosed to conserve diesel.'
          ],
          explanation_ar: 'في خطط الطوارئ، ترتب الموارد دائمًا لحفظ الحياة واستمرارية الوظائف الحيوية التي لا تحتمل أي انقطاع زمني.',
          explanation_en: 'Emergency triage allocates constrained resources strictly by survival criticality and irreversible risk.'
        },
        {
          scenario_ar: 'شركة ناشئة لديها سيولة نقدية تكفي لشهرين فقط، وأمامها 3 مسارات لإنفاق المتبقي: تطوير واجهة جديدة كليًا، أو إطلاق حملة تسويقية غير مجربة، أو إغلاق صفقة أول عميل مؤسسي يدفع مسبقًا.',
          scenario_en: 'A startup has only 2 months of operational runway left and 3 spending options: redesigning the UI, launching an untested ad campaign, or closing a paid pilot contract with an enterprise customer.',
          best_ar: 'تركيز كل الموارد لإغلاق صفقة العميل المؤسسي لتأمين تدفق نقدي فوري يطيل عمر الشركة التشغيلي.',
          best_en: 'Concentrate all resources on closing the enterprise pilot to secure cash flow and extend company runway.',
          distractors_ar: [
            'إنفاق السيولة بالكامل على إعادة تصميم واجهة المستخدم لجذب عدد أكبر من المستثمرين المحتملين في الجولة القادمة.',
            'تقسيم الميزانية المتبقية بالتساوي بين المسارات الثلاثة كإجراء تحوطي يختبر كل الفرضيات السوقية في نفس الوقت.',
            'تجميد جميع النفقات التشغيلية والاحتفاظ بالسيولة المتبقية نقدًا في الحساب البنكي دون السعي وراء أي عميل جديد.'
          ],
          distractors_en: [
            'Direct all remaining cash into a polished UI overhaul to impress potential venture capital investors next quarter.',
            'Split the limited capital equally across all three avenues to hedge and test every commercial hypothesis in parallel.',
            'Freeze all business operations and conserve remaining cash in the bank account without pursuing customer revenue.'
          ],
          explanation_ar: 'البقاء واستدامة التدفق النقدي هو الأولوية الوجودية للمؤسسات ذات السيولة المحدودة، وأي مسار آخر دون تدفق نقدي يعد هدرًا للموارد.',
          explanation_en: 'Securing runway and liquidity is existential for constrained organizations; other investments are secondary.'
        },
        {
          scenario_ar: 'أثناء استجابة فريق أمني لاختراق بيانات نشط، تنافست 3 خطوات: إيقاف الخادم المخترق فورًا لعزل التسريب، أو جمع الأدلة الجنائية، أو صياغة بيان صحفي.',
          scenario_en: 'During a live cybersecurity breach, 3 actions compete: isolate the compromised server to stop data exfiltration, collect forensic disk images, or draft a press release.',
          best_ar: 'عزل الخادم المصاب فورًا لوقف نزيف البيانات، يليه جمع الأدلة الفنية، ثم التواصل الإعلامي وفق الترتيب المنهجي.',
          best_en: 'Isolate the affected server immediately to halt data loss, follow with forensic evidence capture, then handle media communications.',
          distractors_ar: [
            'البدء فورًا بصياغة البيان الصحفي للتحكم في الصورة العامة للشركة قبل أن تتسرب أنباء الحادث إلى منصات التواصل.',
            'إبقاء الخادم المخترق متصلاً بالشبكة لمراقبة تحركات المخترق وجمع الأدلة الرقمية الجنائية الكاملة قبل التدخل.',
            'إيقاف جميع خوادم الشركة وشبكات الفروع فورًا حتى لو أدى ذلك لتعطيل خدمات مئات الآلاف من العملاء غير المتأثرين.'
          ],
          distractors_en: [
            'Prioritize drafting the public statement immediately to control public relations before word leaks to social media.',
            'Keep the compromised server online to monitor attacker behavior and gather comprehensive digital evidence first.',
            'Shut down all company servers globally immediately, taking offline hundreds of thousands of unaffected customers.'
          ],
          explanation_ar: 'الاحتواء الفوري لمنع تفاقم الضرر المباشر هو الخطوة الأولى في بروتوكولات إدارة الحوادث الطارئة قبل أي إجراء توثيقي أو إعلامي.',
          explanation_en: 'Immediate containment of damage is the foundational first step in incident response before documentation or public relations.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على مبادئ إدارة الأولويات وترتيب الأثر: ما هو التوزيع الأمثل للموارد في هذا الموقف؟`,
        questionText_en: `Based on principles of impact triage: What is the optimal allocation of resources in this scenario?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  },

  // 3. Cognitive Empathy & Perspective Taking
  {
    id: 'social_empathy_deduction',
    domain: 'practical_social',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'زميلك في العمل الذي عادة ما يكون متعاونًا ونشيطًا، أصبح منسحبًا ويجيب بكلمات مقتضبة منذ أن تم تعيين قائد جديد للفريق، وعند سؤاله قال: "كل شيء بخير، أنا فقط أؤدي المطلوب مني".',
          scenario_en: 'A colleague who is usually collaborative and energetic has become withdrawn and curt since a new team leader was assigned. When asked, they reply: "Everything is fine, I am just doing what is required."',
          best_ar: 'يشعر بعدم الأمان أو عدم تقدير دوره مع التغيير الجديد، ويبحث عن الاطمئنان دون إظهار ضعفه.',
          best_en: 'They feel insecure or underappreciated with the recent change, seeking reassurance without appearing vulnerable.',
          distractors_ar: [
            'هو راضٍ تمامًا عن المهام المسندة إليه ويفضل تقليص العلاقات الاجتماعية للتركيز في واجباته الأساسية.',
            'هو غير راغب في التطور المهني ويسعى عن قصد للتهرب من المهام المعقدة التي تتطلب مسؤولية أكبر.',
            'هو يخطط لطلب النقل الفوري إلى إدارة أخرى ويرى أن التواصل الروتيني مع الفريق لم يعد مجديًا.'
          ],
          distractors_en: [
            'They are fully satisfied with current duties and deliberately reduce socializing to maximize task throughput.',
            'They lack professional ambition and are intentionally dodging challenging responsibilities requiring accountability.',
            'They plan to request an immediate transfer to another division and consider teamwork engagement obsolete.'
          ],
          explanation_ar: 'الانسحاب المفاجئ وتكرار عبارة "أؤدي المطلوب فقط" يعكسان استجابة دفاعية شائعة للشعور بالتهميش أو عدم الوضوح إثر التغيير الهيكلي.',
          explanation_en: 'Sudden withdrawal and minimal compliance phrases typically signal a defensive emotional reaction to perceived marginalization following structural change.'
        },
        {
          scenario_ar: 'موظف متميز يرفض بشدة ودون مبرر واضح الانتقال من برنامج جداول قديم وبطيء إلى نظام حوسبة سحابي حديث وأكثر كفاءة.',
          scenario_en: 'A high-performing veteran employee vehemently resists migrating from an outdated spreadsheet workflow to an efficient cloud management platform.',
          best_ar: 'يخشى أن يؤدي النظام الجديد إلى فقدانه مكانته وخبرته التراكمية التي بنيت على النظام القديم.',
          best_en: 'They fear the new software will erode their perceived mastery and competence built on the legacy system.',
          distractors_ar: [
            'هو يرغب في تعطيل سير العمل عمداً للضغط على الإدارة للحصول على ترقية أو علاوة سنوية مستحقة.',
            'البرنامج السحابي الجديد يفتقر فعلياً إلى معايير الأمان المعتمدة ويشكل تهديداً قانونياً على استقرار الشركة.',
            'هو يفتقر للذكاء المنطقي الكافي لتعلم الواجهات الحديثة ويعاني من ضعف عام في مهارات الحوسبة المتقدمة.'
          ],
          distractors_en: [
            'They intend to sabotage operational workflow deliberately to pressure management into granting an unscheduled promotion.',
            'The cloud platform is objectively hazardous and introduces documented compliance liabilities into organizational data.',
            'They lack the cognitive aptitude to understand modern interfaces and suffer from a broad deficit in IT skills.'
          ],
          explanation_ar: 'مقاومة الأدوات الجديدة غالبًا ما تنبع من الخوف الضمني من فقدان الكفاءة المتصورة (Competence Insecurity) وليس من العناد غير المبرر.',
          explanation_en: 'Resistance to modernization typically stems from anxiety over losing mastery and status rather than mere obstinacy.'
        },
        {
          scenario_ar: 'عضو مبتدئ في الفريق لا يشارك أبدًا في الاجتماعات الحوارية، ولكنه يرسل مذكرات عمل مكتوبة متقنة ومبتكرة في أوقات متأخرة من الليل.',
          scenario_en: 'A junior teammate never speaks during group brainstorming sessions, but regularly emails sophisticated, highly inventive memos late at night.',
          best_ar: 'يمتلك كفاءة عالية وتفكيرًا تحليليًا عميقًا، لكنه يعاني من القلق الاجتماعي أو الرهبة من التحدث أمام المجموعات الكبيرة.',
          best_en: 'They possess strong analytical capability, but experience social anxiety or intimidation during real-time group meetings.',
          distractors_ar: [
            'هو شخص غير متعاون يفضل العمل المنفرد ويسعى لتهميش دور زملائه عبر التواصل المباشر مع القيادة فقط.',
            'هو يواجه صعوبة حقيقية في فهم أهداف المشاريع ويحتاج إلى ساعات إضافية لترجمة الأفكار إلى سياق عملي.',
            'هو يمتلك نبرة تعالٍ ويرى أن النقاشات الحوارية مضيعة للوقت ولا ترتقي إلى المستوى المنهجي المطلوب لأفكاره.'
          ],
          distractors_en: [
            'They are inherently uncooperative and aim to bypass peers by communicating polished work exclusively to leadership.',
            'They struggle significantly to comprehend project briefs and require excessive nocturnal hours to decipher basic goals.',
            'They adopt an elitist stance, deeming open verbal meetings unproductive and inferior to their analytical standards.'
          ],
          explanation_ar: 'الذكاء الاجتماعي يدرك تباين أنماط التعبير؛ فالكفاءة المعرفية قد تقترن بالحذر الاجتماعي أو تفضيل المعالجة الفردية المتأنية للأفكار.',
          explanation_en: 'Social intelligence recognizes diverse cognitive and communication styles: high competence often pairs with introversion.'
        },
        {
          scenario_ar: 'بعد تأخر غير متوقع في تسليم مرحلة من المشروع، تحول أسلوب العميل من الود والمرونة إلى مخاطبات رسمية مقتضبة ومطالبة بتوثيق كتابي لكل مكالمة.',
          scenario_en: 'Following an unexpected project delay, a client transitions from warm, collaborative communication to strict, formal demands for written records of every conversation.',
          best_ar: 'العميل يشعر بالقلق من المساءلة أمام إدارته ويسعى لحماية نفسه وتوثيق الالتزامات تجنبًا لأي لوم مستقبلي.',
          best_en: 'The client is anxious about accountability to superiors and seeks defensive documentation to protect against future blame.',
          distractors_ar: [
            'العميل يسعى لإلغاء التعاقد واسترداد كافة الدفعات المالية السابقة عبر تصيد الثغرات الإجرائية في التواصل اليومي.',
            'العميل يواجه مشكلات شخصية خارج نطاق العمل انعكست سلبًا على نبرة خطاباته وطريقة تعامله مع فريق التنفيذ.',
            'العميل تلقى توجيهات داخلية بممارسة ضغط نفسي متعمد على الفريق لإجبارهم على تقديم خدمات مجانية إضافية.'
          ],
          distractors_en: [
            'The client is engineering a contractual breach pretext to claw back prior payments by highlighting procedural missteps.',
            'The client is dealing with unrelated external personal stressors that bleed inappropriately into daily workplace correspondence.',
            'The client received internal orders to apply deliberate psychological friction to coerce the vendor into free scope expansion.'
          ],
          explanation_ar: 'اللجوء المفاجئ للشكليات والتوثيق الكتابي الدقيق مؤشر كلاسيكي على شعور الطرف بالتعرض للمخاطر المؤسسية وسعيه للتحوط.',
          explanation_en: 'Sudden formalization and paper-trail enforcement is a classic indicator of institutional risk aversion and self-preservation.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في الموقف: "${item.scenario_ar}" — ما هو التفسير الأكثر عمقًا وفهمًا للدوافع النفسية الكامنة؟`,
        questionText_en: `In this situation: "${item.scenario_en}" — What is the most emotionally intelligent interpretation of the underlying motivations?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  },

  // 4. Decision Making Under Uncertainty
  {
    id: 'social_decision_risk_uncertainty',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 45,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'شركة ناشئة أمامها خياران: الخيار (أ) يمنح ربحًا مؤكدًا ومحدودًا بنسبة 100%، بينما الخيار (ب) يمنح فرصة لربح يعادل 10 أضعاف بنسبة نجاح 60% مع احتمال خسارة 40% من رأس المال التشغيلي الحرج الذي يهدد بقاء الشركة.',
          scenario_en: 'A startup faces two choices: Option A guarantees a modest profit with 100% certainty. Option B offers 10x return with a 60% probability of success, but a 40% risk of losing essential runway capital needed for company survival.',
          best_ar: 'اختيار المسار (أ) أو التحوط للحد من خطر الإفلاس، لأن بقاء المنظمة شرط مسبق لأي ربح مستقبلي.',
          best_en: 'Choose Option A or hedge to eliminate ruin risk, because organizational survival is a prerequisite for future gains.',
          distractors_ar: [
            'المجازفة بالكامل في المسار (ب) لأن القيمة الرياضية المتوقعة للربح المضاعف تفوق احتمالية الخسارة في التحليل الإحصائي.',
            'تأجيل البت في كلا الخيارين والانتظار لعدة أشهر على أمل أن تتحسن الظروف الاقتصادية وتتراجع نسبة المخاطر المحيطة.',
            'تقسيم رأس المال التشغيلي بالتساوي بين المسارين كحل وسط بغض النظر عن خطر تعرض نصف الميزانية للضياع التام.'
          ],
          distractors_en: [
            'Allocate fully into Option B because mathematical expected value favors upside over downside in probability modeling.',
            'Defer deciding on either avenue for months hoping macroeconomic conditions stabilize and remove external volatility.',
            'Split critical runway evenly across both paths as a superficial compromise despite exposing half the treasury to loss.'
          ],
          explanation_ar: 'تجنب خطر الفناء التام (Risk of Ruin) يعلو على الحساب الرياضي البسيط للقيمة المتوقعة عندما يكون رأس المال الحرج مهدداً.',
          explanation_en: 'Avoiding total ruin takes absolute precedence over positive expected value when fundamental existential survival is at stake.'
        },
        {
          scenario_ar: 'قبل إطلاق نظام تجارة إلكترونية بيوم واحد، اكتشف مهندس أمني ثغرة حرجة تسمح بتعديل أسعار المنتجات، بينما يصر مدير التسويق على الإطلاق بسبب حملة إعلانية ممولة بدأت بالفعل.',
          scenario_en: 'One day before launching an e-commerce platform, a security engineer uncovers a critical flaw allowing price tampering. Marketing insists on launching because paid ad campaigns are already active.',
          best_ar: 'تأجيل الإطلاق فورًا حتى ترقيع الثغرة، لأن كلفة الاختراق المالي وتدمير الثقة تفوق بكثير كلفة تأجيل الحملة الإعلانية.',
          best_en: 'Delay launch immediately until patched; financial exposure and reputational ruin far outweigh lost campaign ad spend.',
          distractors_ar: [
            'المضي في الإطلاق المخطط مع محاولة مراقبة المعاملات المشبوهة يدويًا وترقيع الثغرة البرمجية في أول تحديث مجدول لاحقًا.',
            'إطلاق المنصة لعدد محدود من المستخدمين في منطقة جغرافية واحدة مع تعطيل إمكانية الدفع عبر البطاقات الائتمانية مؤقتًا.',
            'تحميل فريق الهندسة الأمنية المسؤولية المالية عن أي خسائر قد تنجم عن الاختراق في حال أصروا على تأجيل الإطلاق التجاري.'
          ],
          distractors_en: [
            'Launch on schedule while attempting manual audit of suspicious orders, deferring the software patch to the next sprint.',
            'Deploy the platform to a restricted pilot cohort in one geography while temporarily disabling credit card gateway processing.',
            'Hold the security team financially accountable for campaign delay penalties if they insist on blocking commercial launch.'
          ],
          explanation_ar: 'القرارات العقلانية تقارن بين الأضرار القصوى غير القابلة للاسترداد (خسائر مفتوحة وسمعة منهارة) وتكاليف التأخير المحدودة والمؤقتة.',
          explanation_en: 'Rational risk management compares catastrophic irreversible ruin against bounded, recoverable delay costs.'
        },
        {
          scenario_ar: 'مستثمر يملك مدخرات حياته فقط ويرغب في استثمارها، ويفاضل بين وضعها كاملة في أسهم شركة صاعدة واحدة ذات نمو صاروخي لكنها شديدة التذبذب، أو توزيعها على صندوق مؤشرات متنوع منخفض التكلفة.',
          scenario_en: 'An individual investor with irreplaceable life savings considers putting everything into a single surging high-volatility tech stock versus a diversified broad-market index fund.',
          best_ar: 'اختيار الصندوق المتنوع لتقليل مخاطر التركيز والانهيار التام لرأس المال الأساسي غير القابل للتعويض.',
          best_en: 'Choose the diversified fund to eliminate idiosyncratic single-point-of-failure risk on irreplaceable capital.',
          distractors_ar: [
            'استثمار المبلغ بالكامل في الشركة الفردية الصاعدة بهدف مضاعفة العائد بأسرع وقت ممكن لتعويض التضخم المالي المتراكم.',
            'الاحتفاظ بكامل المدخرات نقدًا في حساب جارٍ دون استثمار لتفادي أي تراجع في القيمة الاسمية للأموال مهما طال الزمن.',
            'استثمار نصف المبلغ في أسهم الشركة الصاعدة والنصف الآخر في أصول رقمية شديدة المضاربة لتحقيق تنوع عالي العائد.'
          ],
          distractors_en: [
            'Direct the entire sum into the high-growth single firm to accelerate returns and overcome accumulated monetary inflation.',
            'Keep the entirety of savings as uninvested cash in a checking account to avoid any drawdown on nominal capital balance.',
            'Place half the funds into the surging single company and half into speculative crypto assets to maximize risk-adjusted gain.'
          ],
          explanation_ar: 'في نظرية الاستثمار، عندما يكون رأس المال أساسياً ولا يمكن تعويضه، فإن التنويع يقلل التشتت ويمنع الفناء المالي الناتج عن صدمة مفردة.',
          explanation_en: 'Portfolio theory establishes that diversification eliminates unsystematic risk to protect non-recoverable foundational wealth.'
        },
        {
          scenario_ar: 'قائد طائرة مدنية رصد مؤشر تحذير غير مؤكد في نظام الوقود قبل الإقلاع بدقائق، في حين أن الإلغاء سيكلف الشركة غرامات فندقية ويعطل 300 مسافر غاضب.',
          scenario_en: 'An airline captain observes an ambiguous fuel warning indicator minutes before takeoff, knowing aborting will incur heavy passenger accommodations costs and delays.',
          best_ar: 'إلغاء الإقلاع وإجراء الفحص الفني فوراً، لأن سلامة الأرواح تمثل خطراً غير صفري لا يقبل المساومة على التكاليف المالية.',
          best_en: 'Abort takeoff and inspect immediately; human safety represents a zero-tolerance domain where financial costs cannot be traded off.',
          distractors_ar: [
            'مواصلة الإقلاع ومراقبة قراءات خزان الوقود في الجو مع الاستعداد للهبوط الاضطراري في أقرب مطار بديل عند الضرورة.',
            'استدعاء فني الصيانة لإجراء فحص سريع داخل قمرة القيادة دون إنزال الركاب أو إخطار برج المراقبة بتأجيل الرحلة.',
            'الاعتماد على خبرة مساعد الطيار في تقييم مدى احتمالية كون المؤشر عطلاً كهربائياً عابراً في المستشعر وتجاهله إن اتفقا.'
          ],
          distractors_en: [
            'Proceed with takeoff and monitor tank sensors mid-flight, planning a potential emergency divert only if readings escalate.',
            'Summon an on-ramp technician for an informal cockpit inspection without deplaning passengers or informing tower control.',
            'Consult the first officer’s intuition to judge whether the warning is a transient sensor glitch and dismiss it if both concur.'
          ],
          explanation_ar: 'في الأنظمة الحساسة للسلامة (Safety-Critical Systems)، المبدأ الحاكم هو عدم المقامرة بأي خطر كارثي مهما كان احتمال حدوثه ضئيلاً.',
          explanation_en: 'In safety-critical systems, catastrophic asymmetry dictates zero acceptance of fatal tail-risks regardless of probability.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `وفقًا لنظرية القرار العقلاني وإدارة المخاطر: ما هو الموقف الأكثر حكمة واستدامة؟`,
        questionText_en: `Under rational decision theory and risk management: What is the most prudent and sustainable choice?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  },

  // 5. Constructive Feedback Formulation
  {
    id: 'social_constructive_feedback',
    domain: 'practical_social',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'لاحظت أن زميلك يقدم تقارير مليئة بالأخطاء الإملائية والبيانية التي تضعف مصداقية عمل الفريق أمام العملاء.',
          scenario_en: 'You observe that your colleague produces reports containing repeated typographical and data inaccuracies that undermine client confidence in the team.',
          best_ar: 'الحديث معه بشكل فردي وخاص، والإشارة إلى أمثلة محددة مع التركيز على أهمية الحفاظ على ثقة العميل واقتراح آلية مراجعة سريعة.',
          best_en: 'Speak with them privately, referencing specific examples while emphasizing client trust and suggesting a peer review workflow.',
          distractors_ar: [
            'طرح الملاحظات العامة حول دقة التقارير في الاجتماع الأسبوعي للفريق دون ذكر اسمه مباشرة لتفادي الإحراج ونقل الرسالة للجميع.',
            'إرسال بريد إلكتروني رسمي للمدير المباشر لتوثيق الأخطاء المتكررة وطلب إعادة توجيه المهام التحريرية إلى زميل أكثر خبرة ودقة.',
            'تصحيح الأخطاء بصمت في كل تقرير يقدمه دون إبلاغه حرصاً على مشاعره وضماناً لعدم تأثر سير العمل أو توتر العلاقات المهنية.'
          ],
          distractors_en: [
            'Raise general remarks regarding documentation rigor in the weekly group standup without naming him to avoid discomfort.',
            'Dispatch a formal memo to the line manager documenting recurrent mistakes and requesting task reassignment to another editor.',
            'Silently edit and correct all flaws across every file prior to client dispatch to spare feelings and keep work moving.'
          ],
          explanation_ar: 'الخصوصية، والأمثلة الملموسة، والتركيز على النتيجة المشتركة (ثقة العميل) عناصر أساسية لتقبل النقد البناء وتطوير الأداء.',
          explanation_en: 'Privacy, concrete examples, and shared objectives (client trust) form the bedrock of constructive, non-defensive critique.'
        },
        {
          scenario_ar: 'أحد أعضاء الفريق يقاطع زملاءه باستمرار وبشكل عفوي أثناء طرح أفكارهم في جلسات العصف الذهني، مما يمنع الآخرين من إكمال رؤيتهم.',
          scenario_en: 'A teammate habitually interrupts others during brainstorming sessions, breaking their train of thought before ideas are fully expressed.',
          best_ar: 'الاعتراف بحماسه لأفكار زملائه، واقتراح آلية محددة مثل تدوين الملاحظات حتى ينتهي كل متحدث لضمان سماع جميع الآراء.',
          best_en: 'Acknowledge their enthusiasm while proposing a structural rule, like noting thoughts down until each speaker finishes, so all ideas are heard.',
          distractors_ar: [
            'تطبيق لائحة زمنية صارمة تمنح كل متحدث دقيقتين مع فرض صمت تام ومنع أي مداخلات تفاعلية تلقائية بين أعضاء الفريق نهائيًا.',
            'التغاضي التام عن المقاطعات ومواصلة الجلسة مع عقد لقاءات منفصلة لاحقًا لتعويض من لم يتمكن من طرح فكرته كاملة في الاجتماع.',
            'تحويل جلسات العصف الذهني المستقبلية إلى استمارات مكتوبة فردية وإلغاء النقاشات الشفهية المفتوحة لمنع تكرار الاحتكاك الصوتي.',
          ],
          distractors_en: [
            'Enforce a strict two-minute timer per contributor with absolute silence rules, eliminating spontaneous conversational exchanges.',
            'Overlook the interruptions entirely during the session and schedule private follow-ups with teammates who were cut short.',
            'Convert future brainstorming gatherings into asynchronous written forms, abolishing spoken workshops to avoid friction.'
          ],
          explanation_ar: 'تأطير السلوك في إطار دافع إيجابي (الحماس) مع تقديم بديل سلوكي واضح يحافظ على الدافعية ويعالج المشكلة دون إحراج.',
          explanation_en: 'Reframing behavior around a positive intention (enthusiasm) paired with clear process boundaries curbs friction constructively.'
        },
        {
          scenario_ar: 'موظف في فريق العمل عن بُعد لا يقوم بتحديث حالة مهامه في نظام إدارة المشاريع، مما يربك بقية الفريق حول ما تم إنجازه.',
          scenario_en: 'A remote worker rarely updates their task cards in the project management software, leaving colleagues guessing what is finished and what is blocked.',
          best_ar: 'توضيح أثر عدم التحديث على وتيرة عمل زملائه، ومساعدته في دمج التحديث كخطوة أخيرة في روتينه اليومي.',
          best_en: 'Explain how outdated statuses disrupt colleagues’ workflows, and help them anchor updating tasks into their daily end-of-day routine.',
          distractors_ar: [
            'تفعيل نظام تنبيهات آلية مكثفة ترسل إشعارات متكررة كل نصف ساعة إلى بريده الإلكتروني حتى يقوم بتسجيل الإنجاز المطلوب.',
            'تكليف زميل آخر بمتابعة مهامه هاتفيًا بشكل يومي وتحديث اللوحة بالنيابة عنه لضمان دقة البيانات المعروضة في النظام.',
            'إيقاف إسناد أي مهام جديدة له حتى يقوم بتسوية كافة البطاقات العالقة في النظام القديم كإجراء تأديبي لحثه على الالتزام.'
          ],
          distractors_en: [
            'Configure automated bot pings that message his mailbox every half hour until all task fields are marked current.',
            'Appoint an adjacent teammate to ring him daily and log status entries on his behalf to preserve dashboard accuracy.',
            'Cease allocating new work items to his desk until all backlog cards are reconciled as a disciplinary nudge.'
          ],
          explanation_ar: 'الربط بين السلوك الشخصي والأثر المترتب على الآخرين، مصحوبًا ببناء عادة تشغيلية سهلة، يضمن استدامة الالتزام.',
          explanation_en: 'Connecting personal habits to collective impact combined with behavioral habit-stacking creates sustainable adherence.'
        },
        {
          scenario_ar: 'متدرب جديد يعتمد عليك كليًا في كل صغيرة وكبيرة، ويسأل عن كل خطوة قبل المحاولة بنفسه خوفًا من الوقوع في الخطأ.',
          scenario_en: 'A junior intern becomes entirely reliant on you for every small decision, asking for step-by-step validation before trying independently due to fear of failure.',
          best_ar: 'تشجيعه عبر سؤاله: "ما الذي تقترحه أنت أولاً ولماذا؟" وطمأنته بأن التجربة المحسوبة والخطأ جزء طبيعي من التعلم.',
          best_en: 'Empower them by asking "What would you propose first and why?", reassuring them that calculated mistakes are integral to growth.',
          distractors_ar: [
            'تزويده بدليل إرشادي مكتوب من مئة صفحة وإلزامه بعدم طرح أي سؤال شفهي قبل قراءة وتطبيق كل بند فيه بمفرده.',
            'الاستمرار في الإجابة على كافة أسئلته التفصيلية فورًا لتفادي وقوعه في أي أخطاء قد تضر بالعمل في مرحلة تدريبه الحالية.',
            'تكليفه بمشروع مستقل معقد دون أي إشراف مباشر لكسر حاجز الخوف لديه بشكل مفاجئ وإجباره على التصرف تحت الضغط.'
          ],
          distractors_en: [
            'Hand them a 100-page operational manual and instruct them never to speak until every clause has been memorized independently.',
            'Keep answering every micro-question immediately to guarantee no operational errors occur while they are under mentorship.',
            'Assign them an unguided solo enterprise project abruptly without safety nets to force decision-making under intense pressure.'
          ],
          explanation_ar: 'التدريب القيادي الفعال ينقل التابع من الاعتمادية إلى الاستقلالية عبر تحفيز التفكير الاستنتاجي وتوفير بيئة نفسية آمنة.',
          explanation_en: 'Effective mentorship guides mentees from dependency to autonomy through reflective questioning and psychological safety.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في الموقف: "${item.scenario_ar}" — كيف تصيغ التوجيه بأكثر أسلوب يحقق تصحيح السلوك دون إثارة ردود فعل دفاعية؟`,
        questionText_en: `In this situation: "${item.scenario_en}" — How should you deliver feedback to improve behavior without triggering defensiveness?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  },

  // 6. Negotiation Win-Win Compromise
  {
    id: 'social_negotiation_compromise',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'يتنافس قسمان على ميزانية واحدة متبقية لشراء برمجيات: أحدهما يحتاج إلى أداة تحليل بيانات، والآخر يحتاج إلى أداة إدارة مشاريع.',
          scenario_en: 'Two departments compete for a single remaining software budget: one requires a data analysis tool, the other needs a project management suite.',
          best_ar: 'البحث عن منصة متكاملة توفر الميزتين معًا بتكلفة موحدة، أو جدولة الشراء حسب الأسبقية العاجلة مع ضمان الآخر في الدورة القادمة.',
          best_en: 'Search for an integrated platform bundle satisfying both needs, or schedule deployment by immediate urgency with guaranteed funding for the other next cycle.',
          distractors_ar: [
            'تقسيم الميزانية المالية مناصفة بين القسمين لشراء تراخيص تجريبية محدودة المدة لا تلبي المتطلبات التشغيلية لأي منهما.',
            'منح الميزانية للقسم الذي يدر إيرادات مباشرة للشركة وحرمان قسم إدارة المشاريع بحجة أنه مركز تكلفة إداري غير ربحي.',
            'تأجيل شراء أي برمجيات جديدة لكلا القسمين واستثمار المبلغ المتبقي في سندات مالية حتى تتضاعف الميزانية في السنة التالية.'
          ],
          distractors_en: [
            'Split the capital strictly 50/50 so each buys inadequate trial licenses that fail to solve either department’s needs.',
            'Award the full sum solely to the revenue-generating unit while denying project management on the grounds of being an overhead cost.',
            'Freeze software procurement across both teams and park the cash in treasury yields until budgets double next fiscal cycle.'
          ],
          explanation_ar: 'التفاوض التكاملي يبحث عن توسيع القيمة المشتركة أو إيجاد حلول بديلة تلبي المصالح الجوهرية للطرفين بدلاً من الصراع الصفري.',
          explanation_en: 'Integrative bargaining seeks to expand available value or synchronize timelines rather than engaging in zero-sum conflict.'
        },
        {
          scenario_ar: 'مؤسسان لشركة ناشئة يختلفان على توزيع الحصص؛ الأول ابتكر الفكرة وبراءة الاختراع، والثاني يقود العمليات اليومية ويجلب التمويل الأول.',
          scenario_en: 'Two startup co-founders dispute equity split: one conceived the patented invention, while the other drives daily operations and secures seed funding.',
          best_ar: 'اعتماد جدول استحقاق زمني وحركي (Vesting Schedule) يربط الملكية بتحقيق معالم الإنجاز والالتزام المستمر بدلاً من التوزيع العشوائي الفوري.',
          best_en: 'Adopt a milestone-based dynamic vesting schedule that ties ownership to sustained contribution rather than a rigid immediate split.',
          distractors_ar: [
            'منح المبتكر حصة الأغلبية المطلقة (80%) مع تقييد صلاحيات المؤسس التشغيلي في اتخاذ القرارات الإدارية والمالية الكبرى.',
            'تقسيم الأسهم مناصفة (50% لكل منهما) دون أي شروط استحقاق زمني أو معالم إنجاز لضمان شعور كلا الطرفين بالمساواة التامة.',
            'تأجيل تسجيل الشركة رسميًا وتوزيع الحصص حتى تحقيق أول مليون دولار من الإيرادات للتحقق من الجدارة الفعلية لكل شريك.'
          ],
          distractors_en: [
            'Grant the inventor an 80% supermajority while stripping operational leadership of core executive voting power.',
            'Split equity exactly 50/50 upfront with zero vesting restrictions to simulate immediate perceived parity between partners.',
            'Postpone company legal incorporation and equity assignment entirely until reaching seven-figure revenue milestones.'
          ],
          explanation_ar: 'في الشركات الناشئة، توزيع الأسهم المشروط بالاستحقاق الزمني والمعالم العملية يحمي استقرار الشركة ويضمن التزام الشركاء على المدى البعيد.',
          explanation_en: 'Milestone vesting aligns long-term incentives and protects enterprise continuity against premature founder departure.'
        },
        {
          scenario_ar: 'مورد خدمات تقنية يطالب بزيادة سعره بنسبة 15% نتيجة التضخم، بينما ميزانية العميل مجمدة ولا تسمح بأي زيادة نقدية.',
          scenario_en: 'A cloud service vendor demands a 15% rate hike due to inflation, while the client’s IT budget is frozen and cannot absorb higher costs.',
          best_ar: 'عرض تمديد مدة العقد لعدة سنوات مقابل تثبيت السعر الحالي، أو تقليص بعض الميزات غير الأساسية للحفاظ على التكلفة.',
          best_en: 'Offer a multi-year contract extension in exchange for locking current rates, or trim non-essential service tiers to maintain cost.',
          distractors_ar: [
            'رفض الطلب رفضًا قاطعًا وتهديد المورد بإلغاء التعاقد فورًا والتحول إلى مزود خدمة بديل دون دراسة تكلفة الانتقال التقني.',
            'قبول الزيادة السعرية بنسبة 15% كاملة وتحميل العجز المالي الناتج على ميزانية بنود التدريب وتطوير موظفي قسم التقنية.',
            'المطالبة بخصم إضافي بنسبة 10% كرد فعل تفاوضي عكسي لإجبار المورد على التراجع عن طلبه والالتزام بالسعر القديم.'
          ],
          distractors_en: [
            'Flatly reject the hike and threaten immediate cancellation for a competitor without assessing cloud data migration costs.',
            'Accept the entire 15% inflation increase and balance the ledger by draining the employee professional training reserve.',
            'Counter-demand an aggressive 10% price drop as a hardball bluff to coerce the supplier back to existing terms.'
          ],
          explanation_ar: 'المفاوض الماهر يتبادل عناصر مختلفة القيمة (مثل الأفق الزمني أو حجم الخدمات) للتغلب على قيود السعر المباشر.',
          explanation_en: 'Skilled negotiators trade across different currencies of value (such as contract duration or scope) to bypass fixed constraints.'
        },
        {
          scenario_ar: 'فريقان في مؤسسة واحدة يتنافسان على الاستفادة من خبير ذكاء اصطناعي واحد خلال نفس الشهر لتسليم مشروعين حاسمين.',
          scenario_en: 'Two internal project teams compete for the identical senior AI specialist during the same month to meet tight delivery milestones.',
          best_ar: 'تحديد مهام الخبير في بناء الإطار المعماري والنموذج الأساسي لكلا المشروعين بالتتابع، وتفويض التنفيذ الروتيني لمهندسي الفريقين.',
          best_en: 'Direct the specialist to architect the core models for both sequentially, delegating routine integration to each team’s general engineers.',
          distractors_ar: [
            'تخصيص الخبير بالكامل لصالح المشروع الأكبر ميزانية وتأجيل تسليم المشروع الثاني لمدة ثلاثة أشهر لتفادي تشتت تركيزه.',
            'إلزام الخبير بتقسيم ساعات يومه بالتناوب الحاد بين الفريقين (أربع ساعات لكل فريق يومياً) مع حضور كافة الاجتماعات الروتينية.',
            'تكليف مديري المشروعين بإجراء قرعة أسبوعية لتحديد أي فريق يحق له الاستعانة بالخبير طوال الأيام السبعة التالية.'
          ],
          distractors_en: [
            'Allocate the specialist exclusively to the higher-budget project and postpone the secondary project by a quarter.',
            'Mandate the specialist split each day down the middle (4 hours per project) while attending all routine administrative standups.',
            'Have project leaders conduct a weekly coin toss to determine which team monopolizes the specialist for the next 7 days.'
          ],
          explanation_ar: 'إدارة الكفاءات النادرة تقتضي حصر عمل الخبير في النواة عالية الأثر (High-leverage tasks) ونقل المهام الثانوية للمهندسين الآخرين.',
          explanation_en: 'Managing scarce high-value talent requires concentrating their effort on high-leverage bottlenecks while delegating execution.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في الموقف: "${item.scenario_ar}" — ما هو الخيار الذي يحقق أعلى مكسب مشترك ومستدام (Win-Win)؟`,
        questionText_en: `In this situation: "${item.scenario_en}" — What choice best exemplifies integrative win-win negotiation?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  },

  // 7. Professional Ethics & Confidentiality
  {
    id: 'social_ethical_confidentiality',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'طلب منك صديق مقرب يعمل في شركة منافسة تفاصيل حول موعد إطلاق منتج جديد لشركتك، مؤكدًا أنه يسأل فقط من باب الفضول الشخصي.',
          scenario_en: 'A close personal friend employed by a competitor asks you when your company plans to launch its new product, assuring you it is purely personal curiosity.',
          best_ar: 'الاعتذار بلباقة وثبات موضحًا أن معلومات إطلاق المنتجات تخضع لسياسة سرية المعلومات ولا يمكن مشاركتها خارج الفريق.',
          best_en: 'Politely but firmly decline, explaining that launch timelines are governed by confidentiality policy and cannot be shared.',
          distractors_ar: [
            'مشاركته تاريخًا تقريبيًا غير دقيق مع التأكيد عليه بعدم إفشاء هذا السر لأي من زملائه في الشركة المنافسة نهائيًا.',
            'الإفصاح له عن كامل الخطة التسويقية مقابل الحصول على معلومات موازية حول مشاريع شركته المستقبلية لتحقيق تبادل المنفعة.',
            'تجاهل رسالته وقطع التواصل الشخصي معه فورًا وإبلاغ إدارة الموارد البشرية بضرورة مراقبة حساباته على وسائل التواصل.'
          ],
          distractors_en: [
            'Disclose an approximate fuzzy window while emphasizing that he must swear never to repeat it to rival colleagues.',
            'Trade the complete roadmap in exchange for intelligence on his company’s pipeline to establish reciprocity.',
            'Ghost his message, terminate the personal friendship abruptly, and demand HR monitor his external social profiles.'
          ],
          explanation_ar: 'الحدود المهنية الصريحة والمهذبة تحمي الأمانة الوظيفية والسرية المؤسسية دون إضرار غير مبرر بالعلاقات الشخصية.',
          explanation_en: 'Clear, polite boundaries uphold institutional confidentiality and fiduciary duties without unnecessary interpersonal escalation.'
        },
        {
          scenario_ar: 'وصلتك بالخطأ رسالة بريد إلكتروني داخلية تحتوي على جدول رواتب ومكافآت الإدارة العليا غير المخصص للاطلاع العام.',
          scenario_en: 'You accidentally receive an internal email containing executive compensation figures and salary distributions not intended for public viewing.',
          best_ar: 'إخطار المرسل بالخطأ فورًا، وحذف الرسالة دون مشاركتها أو نسخها، والالتزام بالأمانة والسرية المهنية.',
          best_en: 'Notify the sender of the misdirection immediately, delete the email without copying or circulating, and uphold professional integrity.',
          distractors_ar: [
            'الاحتفاظ بنسخة من الملف على وحدة تخزين خارجية آمنة كإجراء احتياطي لاستخدامه في مفاوضات زيادة الراتب السنوية القادمة.',
            'إعادة توجيه الرسالة إلى زميل مقرب موثوق في الفريق لمساعدتك في التحقق مما إذا كانت الأرقام الواردة دقيقة ومنصفة.',
            'طباعة المستند وتسليمه سرًا إلى ممثل نقابة العاملين أو صندوق الشكاوى الداخلي دون مراجعة مرسل الرسالة أولاً.'
          ],
          distractors_en: [
            'Preserve a local backup on external storage as confidential leverage for your upcoming performance review negotiations.',
            'Forward the attachment to a trusted close colleague to audit whether the listed pay scales reflect equitable parity.',
            'Print the leak and hand it anonymously to workplace compliance representatives without informing the original sender.'
          ],
          explanation_ar: 'الأخلاقيات المهنية تقتضي احترام خصوصية البيانات العرضية ومعالجة الخطأ فورًا دون استغلال للمعلومات غير المقصودة.',
          explanation_en: 'Professional ethics mandate respecting accidental disclosures, alerting the originator, and refraining from opportunistic exploitation.'
        },
        {
          scenario_ar: 'أثناء مراجعة حسابات نهاية العام، طلب منك مديرك توقيع اعتماد تاريخ سابق بأثر رجعي على مستند امتثال مالي لتفادي غرامة تأخير إدارية بسيطة.',
          scenario_en: 'During year-end audit reviews, your supervisor asks you to backdate a compliance signature on an audit checklist to avert a minor administrative late fee.',
          best_ar: 'رفض التوقيع بأثر رجعي، واقتراح تسجيل التاريخ الفعلي مع توضيح سبب التأخير الإداري بشفافية في التقرير المرفق.',
          best_en: 'Decline backdating, proposing instead to record the authentic current date with an explanatory transparency note attached.',
          distractors_ar: [
            'الموافقة على التوقيع بالتاريخ القديم استجابة لتوجيهات المدير المباشر طالما أن الغرامة المالية ضئيلة ولن تضر بميزانية الشركة.',
            'طلب تفويض خطي رسمي من المدير يعفيك صراحة من أي مساءلة قانونية قبل الموافقة على تعديل تاريخ التوقيع على المستند.',
            'التوقيع بالاسم المختصر دون توضيح الهوية لتفادي رصد المسؤولية الشخصية من قبل مدققي الحسابات الخارجيين في التقرير السنوي.'
          ],
          distractors_en: [
            'Sign the requested date per managerial instruction, rationalizing that the penalty is negligible and saves operational budget.',
            'Demand an indemnity memo from the supervisor indemnifying you from liability before complying with the backdated entry.',
            'Sign with an ambiguous initial to obscure individual attribution during subsequent external auditor examinations.'
          ],
          explanation_ar: 'النزاهة التوثيقية خط أحمر؛ فالتزوير المالي ولو كان طفيفًا يهدد المساءلة الجنائية ويدمر مصداقية المنظومة بأكملها.',
          explanation_en: 'Documentary veracity is non-negotiable; even minor falsification invites severe legal jeopardy and corrupts organizational auditability.'
        },
        {
          scenario_ar: 'عرض أحد الموردين المتنافسين على مناقصة شركتك دعوة لحضور عطلة فاخرة مدفوعة التكاليف بالكامل تحت مسمى "تعارف مهني" قبل البت في العطاءات.',
          scenario_en: 'A bidding vendor invites you to an all-expenses-paid luxury resort retreat under the guise of "professional networking" before tender decisions are finalized.',
          best_ar: 'رفض الدعوة بلباقة وإخطار لجنة المشتريات والإفصاح عن الموقف لتفادي أي شبهة تعارض مصالح.',
          best_en: 'Politely decline and disclose the invitation to the procurement oversight committee to prevent conflicts of interest.',
          distractors_ar: [
            'قبول الدعوة وحضور الفعالية بشرط عدم مناقشة بنود المناقصة والالتزام التام بالحياد المهني أثناء التقييم النهائي للعروض.',
            'طلب استبدال الرحلة الفاخرة بحضور ورشة تدريبية فنية مجانية يقدمها المورد لفريق العمل في مقر الشركة لتفادي الشبهات.',
            'تجاهل الدعوة دون تقديم أي رد رسمي للمورد ومواصلة تقييم عرضه الفني كالمعتاد مع الاحتفاظ بسجل الدعوة في بريدك الشخصي.'
          ],
          distractors_en: [
            'Attend the resort retreat on condition that tender specifications are not discussed, relying on inner integrity to stay unbiased.',
            'Negotiate converting the luxury vacation into complimentary technical training sessions hosted at your office for staff.',
            'Ignore the invitation without formal response and proceed with bid scoring while archiving the email privately.'
          ],
          explanation_ar: 'تجنب تضارب المصالح يقتضي رفض الهدايا والمزايا التي تؤثر على نزاهة القرار أو تثير الشبهات حول حيادية الاختيار.',
          explanation_en: 'Preventing conflict of interest requires refusing gifts that compromise or appear to compromise impartial decision-making.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في الموقف: "${item.scenario_ar}" — ما هو التصرف المهني والأخلاقي السليم؟`,
        questionText_en: `In this situation: "${item.scenario_en}" — What is the ethical and professionally sound course of action?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  },

  // 8. Cognitive Bias Detection in Decisions
  {
    id: 'social_cognitive_bias_detection',
    domain: 'practical_social',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          scenario_ar: 'أنفقت شركة برمجيات 50 ألف دولار لتطوير تطبيق، ولكن أظهرت دراسة السوق الحالية أن الطلب عليه منعدم ولن يحقق أي عوائد. قال أحد المديرين: "لا يمكننا إيقاف المشروع الآن بعد أن صرفنا 50 ألف دولار!".',
          scenario_en: 'A software company spent $50,000 developing an app. Modern market analysis reveals zero user demand with no prospect of returns. A manager argues: "We cannot abandon the project now after investing $50,000!"',
          best_ar: 'مغالطة التكلفة الغارقة (Sunk Cost Fallacy): تبرير الاستمرار في مشروع خاسر استنادًا إلى نفقات ماضية غير قابلة للاسترداد.',
          best_en: 'Sunk Cost Fallacy: Justifying continued investment in a failing venture based on past unrecoverable expenditures.',
          distractors_ar: [
            'انحياز التأكيد (Confirmation Bias): البحث الانتقائي فقط عن الأدلة والمؤشرات الإيجابية الداعمة للرأي المسبق وإهمال الحقائق المعارضة.',
            'مغالطة الاحتكام إلى السلطة (Appeal to Authority): قبول حجة المدير واعتبارها صائبة استنادًا إلى مكانته الإدارية بدلاً من الأدلة الموضوعية.',
            'انحياز الوضع الراهن (Status Quo Bias): تفضيل بقاء العمليات كما هي وتجنب التغيير التنظيمي خوفًا من تكاليف إعادة الهيكلة والتطوير.'
          ],
          distractors_en: [
            'Confirmation Bias: Selectively gathering positive signals that validate prior convictions while dismissing contradictory market reality.',
            'Appeal to Authority: Accepting executive opinions as objectively sound purely based on hierarchical rank rather than empirical proof.',
            'Status Quo Bias: Preferring operational inertia over restructuring due to anxiety over organizational pivot friction.'
          ],
          explanation_ar: 'القرار العقلاني يقيم التكاليف والعوائد المستقبلية فقط، وتبرير الاستمرار بسبب ما أُنفق سابقًا هو التعريف الدقيق لمغالطة التكلفة الغارقة.',
          explanation_en: 'Rational choices evaluate only future marginal costs and returns; continuing due to past unrecoverable outlays is the classic sunk cost trap.'
        },
        {
          scenario_ar: 'مستثمر اشترى سهمًا بعد أن قرأ فقط المقالات الإيجابية التي تمدح الشركة، وتجاهل عمدًا تقارير التدقيق المحاسبي التي تحذر من تراكم ديونها.',
          scenario_en: 'An investor buys a stock after reading only glowing social media endorsements, deliberately brushing aside audited financial reports detailing soaring debt.',
          best_ar: 'انحياز التأكيد (Confirmation Bias): الميل إلى تصديق والبحث عن المعلومات التي تؤيد رغبات الفرد وإهمال الأدلة المعارضة.',
          best_en: 'Confirmation Bias: The tendency to seek, interpret, and favor evidence conforming to prior beliefs while disregarding contradictory facts.',
          distractors_ar: [
            'تأثير الهالة (Halo Effect): تعميم انطباع إيجابي أولي عن جانب محدد في إدارة الشركة ليشمل كافة مؤشراتها المالية والتشغيلية.',
            'مغالطة المقامر (Gambler\'s Fallacy): الاعتقاد غير المبرر بأن مسار السهم الصاعد سيستمر حتمًا بعد سلسلة طويلة من الارتفاعات السعرية.',
            'انحياز الإدراك المتأخر (Hindsight Bias): الادعاء بعد انهيار سعر السهم بأن المؤشرات كانت واضحة وكان يتوقع النتائج مسبقًا.'
          ],
          distractors_en: [
            'Halo Effect: Projecting an initial positive impression of executive charisma onto the entire technical audit evaluation.',
            'Gambler’s Fallacy: Unfounded conviction that consecutive winning sessions alter underlying independent asset probability curves.',
            'Hindsight Bias: Falsely claiming after a market crash that the collapse was completely predictable and foreseen.'
          ],
          explanation_ar: 'انحياز التأكيد يدفع العقل لتصفية الحقائق وانتقاء ما يوافق هوى الباحث فقط وتجاهل الدلائل القاطعة المخالفة.',
          explanation_en: 'Confirmation bias filters reality by selectively consuming agreeable evidence while screening out refuting data.'
        },
        {
          scenario_ar: 'عرض متجر سيارات سيارة مستعملة بسعر مبدئي 50 ألف دولار، ثم عرض "خصمًا خاصًا" إلى 42 ألف دولار ليشعر المشتري بالصفقة، رغم أن قيمتها الحقيقية في السوق 35 ألف دولار.',
          scenario_en: 'A dealership posts a used car at an initial $50,000, then offers a "special discount" to $42,000 so the buyer feels victorious, despite fair market value being $35,000.',
          best_ar: 'انحياز التثبيت (Anchoring Bias): التأثر المفرط بأول معلومة أو رقم معروض واستخدامه كمرجع لتقييم كافة الخيارات اللاحقة.',
          best_en: 'Anchoring Bias: Over-relying on the initial piece of information offered to calibrate subsequent judgments.',
          distractors_ar: [
            'تأثير التأطير (Framing Effect): تبدل قرار المستهلك بناءً على صياغة العرض في سياق تجنب الخسارة بدلاً من تحقيق مكسب مالي.',
            'انحياز الأثر الأحدث (Recency Bias): إعطاء وزن إدراكي مفرط لآخر سيارة شاهدها المشتري وتجاهل متوسط أسعار السوق الإحصائية.',
            'انحياز التفاؤل المفرط (Optimism Bias): افتراض المشتري بأن هذه السيارة تحديدًا لن تتعرض لأي أعطال ميكانيكية مستقبلية كغيرها.'
          ],
          distractors_en: [
            'Framing Effect: Flipping consumer decisions based on presenting numbers as avoided loss rather than net positive gains.',
            'Recency Bias: Weighting the immediate previous sticker price disproportionately higher than empirical historical market curves.',
            'Optimism Bias: Assuming this specific automobile will be immune to maintenance failures compared to peer fleet statistics.'
          ],
          explanation_ar: 'انحياز التثبيت يجعل العقل يقيس مدى عدالة السعر مقارنة بالرقم الأول المذكور (المرساة) بدلاً من قيمته السوقية المستقلة.',
          explanation_en: 'Anchoring causes human cognitive faculties to assess fairness relative to an arbitrary starting reference point rather than intrinsic value.'
        },
        {
          scenario_ar: 'بعد حادث سقوط طائرة تناقلته الأخبار بكثافة، قرر رجل أعمال السفر بالسيارة لمسافة 2000 كيلومتر، متجاهلاً أن الإحصاءات تثبت أن حوادث الطرق أعلى بمئات الأضعاف من الطيران.',
          scenario_en: 'Following widespread media coverage of a plane crash, an executive chooses to drive 2,000 kilometers instead of flying, ignoring that highway fatality rates are drastically higher.',
          best_ar: 'انحياز التوافر الذهني (Availability Heuristic): تقدير احتمالية حدث ما بناءً على سهولة تذكره واستحضاره في الذاكرة لشدة تأثيره الإعلامي.',
          best_en: 'Availability Heuristic: Overestimating the likelihood of events based on how vividly or easily recent dramatic examples come to mind.',
          distractors_ar: [
            'مغالطة التكلفة الغارقة (Sunk Cost Fallacy): الإصرار على شراء سيارة جديدة للسفر الطويل لتبرير الأموال المنفقة في صيانتها السابقة.',
            'انحياز البقاء (Survivorship Bias): التركيز فقط على السائقين الذين أكملوا رحلاتهم البرية بسلام وتجاهل إحصائيات ضحايا حوادث الطرق.',
            'مغالطة الاحتكام إلى العاطفة (Appeal to Emotion): محاولة إقناع الآخرين بتفضيل القيادة عبر إثارة مشاعر الحنين إلى رحلات الطرق القديمة.'
          ],
          distractors_en: [
            'Sunk Cost Fallacy: Insisting on road trips to validate recent automotive repair expenditures made on a personal vehicle.',
            'Survivorship Bias: Looking exclusively at motorists who finished long cross-country drives unharmed while ignoring fatality statistics.',
            'Appeal to Emotion: Championing vehicular transit over aviation by appealing sentimentally to the romance of the open road.'
          ],
          explanation_ar: 'التوافر الذهني يجعل الأحداث المؤثرة والمصورة حديثًا تبدو شائعة ومحتملة أكثر بكثير من واقعها الإحصائي الحقيقي.',
          explanation_en: 'The availability heuristic misjudges frequency based on emotional salience and ease of recall rather than statistical facts.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.best_ar, en: item.best_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);
      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `في الموقف: "${item.scenario_ar}" — ما هو الانحياز الإدراكي أو المغالطة المنطقية التي يعبر عنها هذا السلوك؟`,
        questionText_en: `In this situation: "${item.scenario_en}" — What cognitive bias or logical fallacy is manifested in this reasoning?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.explanation_ar,
        explanation_en: item.explanation_en
      };
    }
  }
];
